import { LevelGroup } from "./presentlevels_types";

/**
 * Gets the minimum grade order for a level group
 * @param levelGroup The level group to get the minimum grade order for
 * @returns The minimum grade order
 */
export function getMinGradeOrder(levelGroup: LevelGroup): number {
  if (levelGroup.levels.length === 0) {
    throw new Error("Invalid level group: no levels");
  }
  return Math.min(...levelGroup.levels.map(level => level.order));
}

/**
 * Sorts level groups by grade order (descending - higher grades first)
 * @param levelGroups The level groups to sort
 * @returns The sorted level groups
 */
export function sortLevelGroupsByGradeOrder(levelGroups: LevelGroup[]): LevelGroup[] {
  return [...levelGroups].sort((a, b) => {
    const aOrder = getMinGradeOrder(a);
    const bOrder = getMinGradeOrder(b);
    return bOrder - aOrder; // Descending order (higher grades first)
  });
}

/**
 * Finds the leading grade order based on the enrolled grade order
 * @param sortedLevelGroups Level groups sorted by grade order (descending)
 * @param enrolledGradeOrder The order of the enrolled grade
 * @returns The leading grade order
 */
export function findLeadingGradeOrder(sortedLevelGroups: LevelGroup[], enrolledGradeOrder: number): number {
  if (sortedLevelGroups.length === 0) {
    throw new Error("Invalid level group: no levels");
  }

  // Find the first grade that is less than or equal to the enrolled grade
  for (const levelGroup of sortedLevelGroups) {
    const minGradeOrder = getMinGradeOrder(levelGroup);
    if (minGradeOrder <= enrolledGradeOrder) {
      return minGradeOrder;
    }
  }

  // If no grade is found, return the highest available grade
  return getMinGradeOrder(sortedLevelGroups[0]);
}

/**
 * Gets the target grade level groups (the ones that should be displayed by default)
 * @param sortedLevelGroups Level groups sorted by grade order (descending)
 * @param leadingGradeOrder The order of the leading grade
 * @returns The target grade level groups (max 2)
 */
export function getTargetGrades(sortedLevelGroups: LevelGroup[], leadingGradeOrder: number): LevelGroup[] {
  if (sortedLevelGroups.length === 0) {
    return [];
  }

  // Find the index of the leading grade group
  const leadingIndex = sortedLevelGroups.findIndex(group => {
    return getMinGradeOrder(group) === leadingGradeOrder;
  });

  if (leadingIndex === -1) {
    return [];
  }

  // Return the leading grade group and the next group (if available)
  if (leadingIndex < sortedLevelGroups.length - 1) {
    return [sortedLevelGroups[leadingIndex], sortedLevelGroups[leadingIndex + 1]];
  } else {
    // If the leading grade group is the last one, return just that group
    return [sortedLevelGroups[leadingIndex]];
  }
}

/**
 * Gets the grade level groups that are higher than the target grades
 * @param sortedLevelGroups Level groups sorted by grade order (descending)
 * @param leadingGradeOrder The order of the leading grade
 * @returns The higher grade level groups
 */
export function getHigherGrades(sortedLevelGroups: LevelGroup[], leadingGradeOrder: number): LevelGroup[] {
  if (sortedLevelGroups.length === 0) {
    return [];
  }

  // Find the index of the leading grade group
  const leadingIndex = findLevelGroupIndex(sortedLevelGroups, leadingGradeOrder);

  if (leadingIndex === -1) {
    return [];
  }

  // Return all groups above the leading grade group
  return sortedLevelGroups.slice(0, leadingIndex);
}

/**
 * Finds the index of a level group with the given grade order
 */
function findLevelGroupIndex(sortedGroups: LevelGroup[], gradeOrder: number): number {
  return sortedGroups.findIndex(group => getMinGradeOrder(group) === gradeOrder);
}

/**
 * Gets the grade level groups that are lower than the target grades
 * @param sortedLevelGroups Level groups sorted by grade order (descending)
 * @param leadingGradeOrder The order of the leading grade
 * @returns The lower grade level groups
 */
export function getLowerGrades(sortedLevelGroups: LevelGroup[], leadingGradeOrder: number): LevelGroup[] {
  if (sortedLevelGroups.length === 0) {
    return [];
  }

  const leadingIndex = findLevelGroupIndex(sortedLevelGroups, leadingGradeOrder);

  if (leadingIndex === -1) {
    return [];
  }

  // Find the index of the next group (which would be the second target group)
  const nextIndex = leadingIndex + 1;

  // If there's no next group, or it's the last group, return empty array
  if (nextIndex >= sortedLevelGroups.length - 1) {
    return [];
  }

  // Return all groups below the next group
  return sortedLevelGroups.slice(nextIndex + 1);
}

/**
 * Get all grade level groups categorized by their relation to the enrolled grade
 * @param levelGroups The level groups to categorize
 * @param enrolledGradeOrder The enrolled grade order
 * @returns An object containing lowerGrades, targetGrades, and higherGrades arrays
 */
export function getGradeGroups(levelGroups: LevelGroup[], enrolledGradeOrder: number): {
  lowerGrades: LevelGroup[];
  targetGrades: LevelGroup[];
  higherGrades: LevelGroup[];
} {
  // Sort the level groups by grade order (descending)
  const sortedGroups = sortLevelGroupsByGradeOrder(levelGroups);

  // Filter out empty groups
  const nonEmptyGroups = sortedGroups.filter(group => group.levels.length > 0);

  if (nonEmptyGroups.length === 0) {
    return {
      lowerGrades: [],
      targetGrades: [],
      higherGrades: []
    };
  }

  // Find the leading grade order
  const leadingGradeOrder = findLeadingGradeOrder(nonEmptyGroups, enrolledGradeOrder);

  // Special case: If the enrolled grade is lower than all available grades
  // The lowest grade becomes the target, and all others are higher grades
  const lowestGradeOrder = getMinGradeOrder(nonEmptyGroups[nonEmptyGroups.length - 1]);
  if (enrolledGradeOrder < lowestGradeOrder) {
    const lowestGroup = nonEmptyGroups[nonEmptyGroups.length - 1];
    return {
      lowerGrades: [], // No lower grades
      targetGrades: [lowestGroup], // The lowest grade is the target
      higherGrades: nonEmptyGroups.slice(0, nonEmptyGroups.length - 1) // All other grades are higher
    };
  }

  // Get the target, higher, and lower grade groups
  const targetGrades = getTargetGrades(nonEmptyGroups, leadingGradeOrder);
  const higherGrades = getHigherGrades(nonEmptyGroups, leadingGradeOrder);
  const lowerGrades = getLowerGrades(nonEmptyGroups, leadingGradeOrder);

  return {
    lowerGrades,
    targetGrades,
    higherGrades
  };
}
